import React from "react";
import { Trans, useTranslation } from "react-i18next";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseButton from "@reusables/BaseButton";
import BaseBadge from "@reusables/BaseBadge";
import BaseIntegrationInject from "@reusables/BaseIntegrationInject";
import { InvoiceApproval } from "@/types/general";
import { useSetReadyForPaymentMutation } from "@redux/features/invoiceApproval/invoiceApprovalApi";
import { toastError } from "@helpers/utils";
import { toast } from "react-toastify";

type SetReadyForPaymentModalProperties = {
    isOpen: boolean;
    onClose: () => void;
    purchaseOrderId: number;
    invoice?: {
        systemId: number;
        system: InvoiceApproval.DTO.Unlink["system"];
        number: number;
    }
}
export default function SetReadyForPaymentModal(props: SetReadyForPaymentModalProperties) {
    const { t } = useTranslation("", { keyPrefix: "purchaseOrder.general.modals.setReadyForPayment" });

    const [setReadyForPayment, { isLoading }] = useSetReadyForPaymentMutation();

    if (!props.invoice) return null;

    return (
        <BaseModal
            isOpen={props.isOpen}
            onClose={props.onClose}
            width={700}
            padding="56px"
            useCloseIcon
        >
            <div className="space-y-[32px]">
                <div className="text-xl font-semibold text-center text-accent">{t("heading")}</div>
                <div className="flex items-center justify-center space-x-1 text-accent">
                    <Trans i18nKey="subheading" t={t} components={{
                        invoice: <InvoiceBadge system={props.invoice.system} number={props.invoice.number} />
                    }} />
                </div>
                <div className="grid grid-cols-2 gap-[16px]">
                    <BaseButton text={t("buttons.cancel")} size="md" primaryOutlined onClick={props.onClose} />
                    <BaseButton
                        text={t("buttons.unlink")}
                        size="md"
                        onClick={() => {
                            if (!props.invoice) return;

                            setReadyForPayment({
                                purchase_order_id: props.purchaseOrderId,
                                system: props.invoice.system,
                                system_id: props.invoice.systemId
                            }).unwrap()
                                .then(() => {
                                    toast.success(t("responses.success"));
                                    props.onClose();
                                })
                                .catch(toastError);
                        }}
                        loading={isLoading}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

function InvoiceBadge(props: { system: InvoiceApproval.DTO.Unlink["system"], number: number }) {
    return (
        <BaseBadge className="px-[8px] mx-1">
            <div className="flex items-center space-between space-x-2">
                <BaseIntegrationInject integration={props.system}>
                    {
                        (integration) =>
                            <img
                                src={integration?.imageUrl}
                                alt={integration?.name}
                                className="w-4 h-4 p-[2px] bg-white rounded-sm object-contain"
                            />
                    }
                </BaseIntegrationInject>
                <div>{props.number}</div>
            </div>
        </BaseBadge>
    );
}
